import React from "react";
import { Interview } from "@/services/interviewService";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Calendar, Building, User } from "lucide-react";
import { format } from "date-fns";

interface TodayInterviewsBannerProps {
  interviews: Interview[];
}

const TodayInterviewsBanner: React.FC<TodayInterviewsBannerProps> = ({
  interviews,
}) => {
  // Filter interviews scheduled for today
  const today = new Date();
  const todayInterviews = interviews.filter((interview) => {
    if (
      !interview.date ||
      (interview.status !== "scheduled" &&
        interview.status !== "follow-up scheduled")
    )
      return false;

    const interviewDate =
      interview.date instanceof Date
        ? interview.date
        : new Date(interview.date);

    return interviewDate.toDateString() === today.toDateString();
  });

  // Sort by time
  const sortedTodayInterviews = todayInterviews.sort((a, b) => {
    const dateA = a.date instanceof Date ? a.date : new Date(a.date!);
    const dateB = b.date instanceof Date ? b.date : new Date(b.date!);
    return dateA.getTime() - dateB.getTime();
  });

  // Format time for display
  const formatTime = (date: Date | string | undefined) => {
    if (!date) return "Time TBD";
    const dateObj = date instanceof Date ? date : new Date(date);
    return format(dateObj, "h:mm a");
  };

  if (sortedTodayInterviews.length === 0) {
    // Thin grey banner when no interviews
    return (
      <Card className="mb-4 shadow-sm">
        <CardContent className="p-2">
          <div className="flex items-center justify-center text-sm text-muted-foreground">
            <Calendar className="h-4 w-4 mr-2" />
            No interviews scheduled for today
          </div>
        </CardContent>
      </Card>
    );
  }

  // Expanded view when there are interviews
  return (
    <Card className="mb-4 shadow-sm border-l-4 border-l-ivy-green">
      <CardContent className="p-4">
        <div className="flex items-center mb-3">
          <Calendar className="h-5 w-5 mr-2 text-green-600 dark:text-green-400" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            Today's Scheduled Interviews ({sortedTodayInterviews.length})
          </h3>
        </div>

        <div className="space-y-3">
          {sortedTodayInterviews.map((interview) => (
            <div
              key={interview.id}
              className="flex items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800"
            >
              {/* Primary Info: Time and Angle */}
              <div className="flex items-center space-x-6 mr-6">
                {/* Time - Most prominent */}
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-gray-700 dark:text-gray-300" />
                  <span className="text-lg font-bold text-gray-700 dark:text-gray-300">
                    {formatTime(interview.date)}
                  </span>
                </div>

                {/* Angle - Second most prominent */}
                {interview.angle && (
                  <div className="flex items-center">
                    <span className="text-base font-semibold text-gray-800 dark:text-gray-200">
                      {interview.angle}
                    </span>
                  </div>
                )}
              </div>

              {/* Secondary Info: Name, Company, Title */}
              <div className="flex-grow">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">
                    {interview.expertFirstName} {interview.expertLastName}
                  </span>
                  {" • "}
                  <span>{interview.title}</span>
                  {" • "}
                  <span>{interview.company}</span>
                </div>

                {interview.network && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    via {interview.network}
                  </div>
                )}
              </div>

              {/* Duration badge - smaller */}
              <div className="flex-shrink-0 ml-4">
                <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full">
                  {interview.duration} min
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default TodayInterviewsBanner;
